// World-space name tags rendered over each character's head. DOM-based —
// each tag is a <div> containing inline SVG, positioned via translate() from
// a 3D→2D projection done each frame. Tags scale with depth so a faraway
// teammate reads smaller, matching the screenshot.
//
// Colour source is TEAM color (not individual character color), because the
// reference screenshot shows YOU in red even though the character body is
// orange — the label is a team identifier, not a palette echo.

import type { Renderer } from '../render/Renderer';
import type { MatchState, TeamMember } from '../sim/MatchState';
import { TEAM_COLORS } from '../sim/MatchState';
import type { SimState } from '../sim/SimState';
import { t } from '../i18n';

// Visual tuning constants.
const BASE_WIDTH_YOU = 80;
const BASE_HEIGHT = 60;
const NAME_FONT_SIZE = 22;
const NAME_PADDING_X = 22;
const CHAR_WIDTH = 11; // rough advance width of the Chewy font at 22px
const LIFT_ABOVE_PELVIS_Y = 1.55; // world-space Y offset above pelvis
// Scale falls off from 1.0 (close) toward SCALE_FAR (far) to give perspective.
const SCALE_NEAR_DIST = 3;
const SCALE_FAR_DIST = 18;
const SCALE_FAR = 0.5;

export class NameTags {
  private container: HTMLElement;
  private tagEls = new Map<number, HTMLElement>();

  constructor(private renderer: Renderer, private match: MatchState) {
    const root = document.getElementById('hud')!;
    this.container = document.createElement('div');
    this.container.id = 'name-tags';
    this.container.style.cssText = `
      position: fixed; inset: 0; pointer-events: none; z-index: 1;
      font-family: 'Chewy', ui-sans-serif, system-ui, sans-serif;
    `;
    root.appendChild(this.container);

    for (const m of this.match.members) {
      const el = document.createElement('div');
      el.className = 'name-tag';
      el.style.cssText = `
        position: absolute; left: 0; top: 0;
        will-change: transform, opacity;
        transition: opacity 180ms ease;
        transform-origin: 50% 100%;
      `;
      el.innerHTML = renderTagSvg(m);
      this.container.appendChild(el);
      this.tagEls.set(m.id, el);
    }
  }

  update(snap: SimState): void {
    for (const m of this.match.members) {
      const el = this.tagEls.get(m.id);
      if (!el) continue;
      const cs = snap.characters.find((c) => c.id === m.id);
      const dead = !cs || cs.wasted || cs.ringedOut;
      if (dead) { el.style.opacity = '0'; continue; }
      const { x, y, onScreen, depth01 } = this.renderer.worldToScreen(
        cs.pelvisX, cs.pelvisY + LIFT_ABOVE_PELVIS_Y, cs.pelvisZ
      );
      if (!onScreen) { el.style.opacity = '0'; continue; }
      // depth01 is [0,1] from near→far. Use the actual world distance to the
      // camera for scale so the falloff reads consistently across maps.
      const cam = this.renderer.cameraRig.camera.position;
      const dx = cs.pelvisX - cam.x;
      const dy = cs.pelvisY - cam.y;
      const dz = cs.pelvisZ - cam.z;
      const dist = Math.hypot(dx, dy, dz);
      const t = clamp01((dist - SCALE_NEAR_DIST) / (SCALE_FAR_DIST - SCALE_NEAR_DIST));
      const scale = 1 + (SCALE_FAR - 1) * t;
      el.style.opacity = '1';
      el.style.transform = `translate3d(${x}px, ${y}px, 0) translate(-50%, -100%) scale(${scale.toFixed(3)})`;
      // Paint order: closer labels on top. Using depth01 directly gives
      // near labels a higher z-index so they occlude distant ones.
      el.style.zIndex = String(10000 - Math.floor(depth01 * 10000));
      void depth01;
    }
  }

  dispose(): void {
    this.container.remove();
  }
}

function renderTagSvg(m: TeamMember): string {
  const team = TEAM_COLORS[m.team];
  const text = m.isLocal ? t('hud.you') : m.name;
  // Keep the shield-style pin from renderHeadLabelSvg but parameterise the
  // width — name labels in the screenshot are wider than the local-player
  // "YOU" pin and have a thinner tail. Long names just expand the body.
  const textWidthPx = Math.max(
    BASE_WIDTH_YOU,
    NAME_PADDING_X * 2 + text.length * CHAR_WIDTH
  );
  const w = textWidthPx;
  const h = BASE_HEIGHT;
  // The tail triangle sits under the body and points down. Keep it
  // roughly the same size regardless of body width.
  const tipW = 18;
  const tipH = 14;
  const cx = w / 2;
  const body = `M 12 2 H ${w - 12} A 10 10 0 0 1 ${w - 2} 12 V 32 Q ${w - 2} 40 ${w - 10} 42 L ${cx + tipW / 2} ${42} Q ${cx} ${42 + tipH} ${cx - tipW / 2} 42 L 10 42 Q 2 40 2 32 V 12 A 10 10 0 0 1 12 2 Z`;
  const gradId = `nt-${m.id}`;
  const lighter = adjustLuminance(team.primary, +0.18);
  const darker = adjustLuminance(team.primary, -0.22);
  return `
    <svg viewBox="0 0 ${w} ${h}" width="${w}" height="${h}" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="${gradId}" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="${lighter}"/>
          <stop offset="55%" stop-color="${team.primary}"/>
          <stop offset="100%" stop-color="${darker}"/>
        </linearGradient>
      </defs>
      <path d="${body}" fill="url(#${gradId})" stroke="${team.accent}" stroke-width="3" stroke-linejoin="round"/>
      <text x="${cx}" y="24" fill="#FFFFFF" stroke="${team.accent}" stroke-width="3"
            text-anchor="middle" dominant-baseline="central"
            font-family="Chewy, sans-serif" font-size="${NAME_FONT_SIZE}"
            style="paint-order: stroke fill;">${escapeSvgText(text)}</text>
    </svg>
  `;
}

function escapeSvgText(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function clamp01(v: number): number { return Math.max(0, Math.min(1, v)); }

/** Tweak a hex color's luminance in HSL space by `delta` (negative = darker,
 *  positive = brighter). Small helper so the pin gradient always has a
 *  reasonable two-tone falloff regardless of team primary. */
function adjustLuminance(hex: string, delta: number): string {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  let hHue = 0, s = 0;
  if (d !== 0) {
    s = d / (1 - Math.abs(2 * l - 1));
    switch (max) {
      case r: hHue = ((g - b) / d) % 6; break;
      case g: hHue = (b - r) / d + 2; break;
      default: hHue = (r - g) / d + 4; break;
    }
    hHue *= 60;
    if (hHue < 0) hHue += 360;
  }
  const l2 = Math.max(0, Math.min(1, l + delta));
  const c = (1 - Math.abs(2 * l2 - 1)) * s;
  const x = c * (1 - Math.abs(((hHue / 60) % 2) - 1));
  const m = l2 - c / 2;
  let rr = 0, gg = 0, bb = 0;
  if (hHue < 60)      [rr, gg, bb] = [c, x, 0];
  else if (hHue < 120) [rr, gg, bb] = [x, c, 0];
  else if (hHue < 180) [rr, gg, bb] = [0, c, x];
  else if (hHue < 240) [rr, gg, bb] = [0, x, c];
  else if (hHue < 300) [rr, gg, bb] = [x, 0, c];
  else                 [rr, gg, bb] = [c, 0, x];
  const to = (v: number): string => Math.round((v + m) * 255).toString(16).padStart(2, '0');
  return `#${to(rr)}${to(gg)}${to(bb)}`;
}
